import { useState, useRef, useEffect } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { RealtimeRole } from '@doxyme/realtime';
import { CallState } from '../../features/call/call.types';
import { getRole } from '../../features/realtime/realtime.selectors';
import { useAppSelector } from '../useReduxToolkit';
import { useCallState } from '../useCallState';
import { getIsCallLoading, getIsCallLeaving } from '../useCallState/helpers';
import { usePatientCallResumedNotification } from './usePatientCallResumedNotification';

export type UsePatientCallHoldReturn = {
  /**
   * Indicates whether the call is currently on hold.
   */
  isCallOnHold: boolean;
  /**
   * Indicates whether the call has resumed and is in progress.
   * This is used to conditionally display elements related to an active call after it has resumed.
   */
  isCallResumed: boolean;
};

const hookInitValues = {
  isCallOnHold: false,
  isCallResumed: false,
};

export const usePatientCallHoldImpl = (): UsePatientCallHoldReturn => {
  const { callState } = useCallState();
  const role = useAppSelector(getRole);
  const isPatient = role === RealtimeRole.Patient;

  const [isCallOnHold, setIsCallOnHold] = useState(false);
  const [isCallResumed, setIsCallResumed] = useState(false);
  const prevIsCallOnHold = useRef(false);

  usePatientCallResumedNotification({ isCallResumed });

  useEffect(() => {
    if (!isPatient) {
      return;
    }

    if (getIsCallLeaving(callState)) {
      setIsCallOnHold(false);
      setIsCallResumed(false);
      return;
    }

    if (callState === CallState.OnHold) {
      setIsCallOnHold(true);
      setIsCallResumed(false);
      prevIsCallOnHold.current = true;
      return;
    }

    if (prevIsCallOnHold.current) {
      if (getIsCallLoading(callState)) {
        setIsCallOnHold(false);
        setIsCallResumed(false);
      } else {
        setIsCallOnHold(false);
        setIsCallResumed(true);
        prevIsCallOnHold.current = false;
      }
    }
  }, [callState, isPatient]);

  return {
    isCallOnHold,
    isCallResumed,
  };
};

/**
 * Manages the patient's state when a call is placed on hold and resumed.
 * Notifies a patient when the call is placed on hold and when it resumes.
 *
 * @returns {UsePatientCallHoldReturn}
 * - `isCallOnHold` - Indicates whether the call is currently on hold.
 * - `isCallResumed` - Indicates whether the call has resumed and is currently in progress.
 */
export const usePatientCallHold = singletonHook<UsePatientCallHoldReturn>(
  hookInitValues,
  usePatientCallHoldImpl,
);
